import { ThemeTokenSample, themeTokenAnchor } from '@/components/builder/token-navigation';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SEED_THEME } from '@/lib/seed-theme';
import type { Asset } from '@/lib/queries';
import {
  addThemeToken,
  unusedColorTokens,
  collectThemeTokenReferences,
  deleteThemeToken,
  duplicateThemeToken,
  renameThemeToken,
  type AuthoringDoc,
  type SessionStore,
  type ThemeTokenFamily,
} from '@personalizehq/editor';
import type { ButtonVariant, Corners, FontToken, Gradient, Shadow, Theme } from '@personalizehq/schemas/ir';
import { ChevronDown, Copy, MoreHorizontal, Plus, Search, Settings2, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useStore } from 'zustand';

type ThemeTab = 'overview' | ThemeTokenFamily;
type TokenTarget = { family: ThemeTokenFamily; token: string };

// The panel's information architecture (ADR 0014). FOUNDATIONS are the primitive design tokens;
// COMPONENTS are the tier-3 tokens that bundle them — today just `buttons`. Grouping them stops a
// component token from masquerading as a foundational one next to colour and type.
const FOUNDATION_FAMILIES = ['colors', 'fonts', 'radii', 'elevations', 'gradients'] as const;
const FAMILY_LABEL: Record<ThemeTokenFamily, string> = {
  colors: 'Colors',
  fonts: 'Type',
  radii: 'Radius',
  elevations: 'Elevation',
  gradients: 'Gradient',
  buttons: 'Buttons',
};
// The singular noun for a token of each family. `radii`.slice(0, -1) is "radi", so this is a map,
// not a chop.
const FAMILY_SINGULAR: Record<ThemeTokenFamily, string> = {
  colors: 'color',
  fonts: 'font',
  radii: 'radius',
  elevations: 'elevation',
  gradients: 'gradient',
  buttons: 'button',
};
type ThemeGroup = 'overview' | 'foundations' | 'components';
const groupOf = (tab: ThemeTab): ThemeGroup =>
  tab === 'overview' ? 'overview' : tab === 'buttons' ? 'components' : 'foundations';

export function ThemePanel({
  session,
  assets,
  appTheme,
  savingDefaults,
  focusedToken,
  onFocusedTokenHandled,
  onSaveDefaults,
}: {
  session: SessionStore;
  assets: Asset[];
  appTheme: Theme | null | undefined;
  savingDefaults: boolean;
  focusedToken?: TokenTarget | null;
  onFocusedTokenHandled?: () => void;
  onSaveDefaults: (theme: Theme) => void;
}): React.JSX.Element {
  const doc = useStore(session, (state) => state.doc);
  // A focused-token request keys/remounts this panel at the route boundary. Initializing from it
  // avoids a set-state-in-effect cascade while still rendering the requested tab on the first pass.
  const [tab, setTab] = useState<ThemeTab>(focusedToken?.family ?? 'overview');
  const [query, setQuery] = useState('');
  const [newName, setNewName] = useState('');
  const [expanded, setExpanded] = useState<string | null>(focusedToken?.token ?? null);
  const [renameTarget, setRenameTarget] = useState<TokenTarget | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<TokenTarget | null>(null);
  const [replacement, setReplacement] = useState('');
  const [defaultsAction, setDefaultsAction] = useState<'replace' | 'save' | null>(null);
  const references = useMemo(() => collectThemeTokenReferences(doc), [doc]);
  const proposedDefaults = appTheme ?? SEED_THEME;
  const defaultsAvailable = appTheme !== undefined;

  useEffect(() => {
    if (!focusedToken) return;
    window.requestAnimationFrame(() => {
      const target = document.getElementById(themeTokenAnchor(focusedToken.family, focusedToken.token));
      target?.scrollIntoView({ block: 'center' });
      target?.focus({ preventScroll: true });
      onFocusedTokenHandled?.();
    });
  }, [focusedToken, onFocusedTokenHandled]);

  const family = tab === 'overview' ? null : tab;
  const group = groupOf(tab);
  const missingFromDefaults = references.filter(
    (reference) =>
      reference.nodeId &&
      !Object.prototype.hasOwnProperty.call(proposedDefaults[reference.family], reference.token)
  );
  const invalidDefaultButtonReferences = Object.entries(proposedDefaults.buttons).flatMap(([name, button]) =>
    [button.bg, button.fg]
      .filter((token): token is string => Boolean(token))
      .filter((token) => !Object.prototype.hasOwnProperty.call(proposedDefaults.colors, token))
      .map((token) => `${name} → ${token}`)
  );

  const apply = (mutate: (draft: AuthoringDoc) => void, mergeKey?: string): void => {
    session.getState().applyChange(mutate, mergeKey);
  };

  function createToken(): void {
    if (!family) return;
    try {
      const name = newName.trim();
      // Button variants, elevations, and gradients all reference colour tokens, so a colour must
      // exist first — the same guard, one message.
      if ((family === 'buttons' || family === 'elevations' || family === 'gradients') && Object.keys(doc.theme.colors).length === 0) {
        throw new Error(`Create a color token before adding a ${FAMILY_SINGULAR[family]}.`);
      }
      apply((draft) => {
        const firstColor = Object.keys(draft.theme.colors)[0]!;
        if (family === 'colors') addThemeToken(draft, family, name, { light: '#000000', dark: '#ffffff' });
        if (family === 'fonts') {
          addThemeToken(draft, family, name, {
            family: 'Inter',
            size: 16,
            weight: 400,
            line_height: 1.5,
            max_scale: 1.6,
          });
        }
        if (family === 'buttons') {
          addThemeToken(draft, family, name, {
            bg: firstColor,
            fg: firstColor,
            radius: 8,
            padding: { top: 10, end: 16, bottom: 10, start: 16 },
          });
        }
        if (family === 'radii') addThemeToken(draft, family, name, 12);
        if (family === 'elevations') addThemeToken(draft, family, name, { y: 4, blur: 12, color: firstColor });
        if (family === 'gradients') addThemeToken(draft, family, name, { kind: 'linear', angle: 180, stops: [firstColor, firstColor] });
      });
      setNewName('');
      setExpanded(name);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Token could not be created.');
    }
  }

  function mergeDefaults(): void {
    if (!defaultsAvailable) return;
    apply((draft) => {
      (['colors', 'fonts', 'buttons', 'radii', 'elevations', 'gradients'] as const).forEach((tokenFamily) => {
        Object.entries(proposedDefaults[tokenFamily]).forEach(([name, value]) => {
          if (!Object.prototype.hasOwnProperty.call(draft.theme[tokenFamily], name)) {
            // The family loop preserves the concrete value type at runtime; Theme is JSON-only.
            (draft.theme[tokenFamily] as Record<string, unknown>)[name] = structuredClone(value);
          }
        });
      });
    });
    toast.success('Added missing app-default tokens.');
  }

  const renderTokenList = (tokenFamily: ThemeTokenFamily): React.JSX.Element => (
    <TokenList
      family={tokenFamily}
      theme={doc.theme}
      assets={assets}
      query={query}
      references={references}
      expanded={expanded}
      onExpandedChange={(token) => setExpanded((current) => (current === token ? null : token))}
      onChange={(token, value, field) =>
        apply((draft) => {
          (draft.theme[tokenFamily] as Record<string, unknown>)[token] = value;
        }, `theme.${tokenFamily}.${token}.${field}`)
      }
      onDuplicate={(token) => {
        let duplicate = '';
        apply((draft) => { duplicate = duplicateThemeToken(draft, tokenFamily, token); });
        setExpanded(duplicate);
        toast.success(`Duplicated “${token}”.`);
      }}
      onRename={(token) => { setRenameTarget({ family: tokenFamily, token }); setRenameValue(token); }}
      onDelete={(token) => { setDeleteTarget({ family: tokenFamily, token }); setReplacement(''); }}
    />
  );

  return (
    <Tabs value={group} onValueChange={(value) => setTab(value === 'overview' ? 'overview' : value === 'components' ? 'buttons' : 'colors')} className="min-h-0">
      <div className="sticky top-0 z-10 space-y-2 border-b bg-background p-2">
        <div className="flex items-center gap-1.5">
          <TabsList className="grid h-8 min-w-0 flex-1 grid-cols-3 p-0.5">
            <TabsTrigger className="px-1 text-[10px]" value="overview">Overview</TabsTrigger>
            <TabsTrigger className="px-1 text-[10px]" value="foundations">Foundations</TabsTrigger>
            <TabsTrigger className="px-1 text-[10px]" value="components">Components</TabsTrigger>
          </TabsList>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8 shrink-0" aria-label="Manage theme">
                <Settings2 className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={!defaultsAvailable} onSelect={mergeDefaults}>Merge app defaults</DropdownMenuItem>
              <DropdownMenuItem disabled={!defaultsAvailable} onSelect={() => setDefaultsAction('replace')}>
                Replace from app defaults
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setDefaultsAction('save')}>Save as app defaults</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {group === 'foundations' ? (
          <div role="tablist" aria-label="Foundation token families" className="flex gap-1 overflow-x-auto">
            {FOUNDATION_FAMILIES.map((f) => (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={tab === f}
                onClick={() => setTab(f)}
                className={`shrink-0 rounded px-2 py-1 text-[10px] transition-colors ${tab === f ? 'bg-secondary font-medium text-foreground' : 'text-muted-foreground hover:bg-secondary/50'}`}
              >
                {FAMILY_LABEL[f]}
              </button>
            ))}
          </div>
        ) : null}

        {family ? (
          <>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={`Search ${family === 'fonts' ? 'type' : family}`}
                aria-label={`Search ${family}`}
                className="h-8 pl-7 text-xs"
              />
            </div>
            <form
              className="flex gap-1.5"
              onSubmit={(event) => {
                event.preventDefault();
                createToken();
              }}
            >
              <Input
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="New token name"
                aria-label={`New ${FAMILY_SINGULAR[family]} token name`}
                className="h-8 min-w-0 text-xs"
              />
              <Button type="submit" variant="outline" size="icon" className="size-8 shrink-0" disabled={!newName.trim()}>
                <Plus className="size-3.5" />
                <span className="sr-only">Add {FAMILY_SINGULAR[family]} token</span>
              </Button>
            </form>
          </>
        ) : null}
      </div>

      <TabsContent value="overview" className="m-0 space-y-2 p-2">
        <ThemeOverview theme={doc.theme} references={references} onOpen={setTab} />
      </TabsContent>
      <TabsContent value="foundations" className="m-0 p-2">
        {family === 'colors' ? <UnusedColors doc={doc} apply={apply} /> : null}
        {family && family !== 'buttons' ? renderTokenList(family) : null}
      </TabsContent>
      <TabsContent value="components" className="m-0 p-2">
        {renderTokenList('buttons')}
      </TabsContent>

      <Dialog open={renameTarget !== null} onOpenChange={(open) => !open && setRenameTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename token</DialogTitle>
            <DialogDescription>Every reference in this experience will be updated in the same undo step.</DialogDescription>
          </DialogHeader>
          <Input value={renameValue} onChange={(event) => setRenameValue(event.target.value)} aria-label="New token name" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button
              disabled={!renameValue.trim()}
              onClick={() => {
                if (!renameTarget) return;
                try {
                  apply((draft) => renameThemeToken(draft, renameTarget.family, renameTarget.token, renameValue));
                  setExpanded(renameValue.trim());
                  setRenameTarget(null);
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : 'Token could not be renamed.');
                }
              }}
            >
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteTokenDialog
        target={deleteTarget}
        theme={doc.theme}
        referenceCount={deleteTarget ? references.filter((ref) => ref.family === deleteTarget.family && ref.token === deleteTarget.token).length : 0}
        replacement={replacement}
        onReplacementChange={setReplacement}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          try {
            apply((draft) => deleteThemeToken(draft, deleteTarget.family, deleteTarget.token, replacement || undefined));
            setDeleteTarget(null);
            setExpanded(null);
          } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Token could not be deleted.');
          }
        }}
      />

      <Dialog open={defaultsAction !== null} onOpenChange={(open) => !open && setDefaultsAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{defaultsAction === 'save' ? 'Save as app defaults?' : 'Replace this experience theme?'}</DialogTitle>
            <DialogDescription>
              {defaultsAction === 'save'
                ? 'New experiences will start from this theme. Existing experiences and published campaigns will not change.'
                : invalidDefaultButtonReferences.length > 0
                  ? `${invalidDefaultButtonReferences.length} app-default button reference${invalidDefaultButtonReferences.length === 1 ? '' : 's'} point to missing colors. Repair the app defaults before replacing this theme.`
                  : missingFromDefaults.length > 0
                  ? `${missingFromDefaults.length} current component reference${missingFromDefaults.length === 1 ? '' : 's'} would be unresolved. Add matching app-default tokens or use Merge instead.`
                  : 'The current experience theme will be replaced from app defaults in one undoable change.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDefaultsAction(null)}>Cancel</Button>
            <Button
              disabled={savingDefaults || (defaultsAction === 'replace' && (missingFromDefaults.length > 0 || invalidDefaultButtonReferences.length > 0))}
              onClick={() => {
                if (defaultsAction === 'save') onSaveDefaults(structuredClone(doc.theme));
                if (defaultsAction === 'replace') apply((draft) => { draft.theme = structuredClone(proposedDefaults); });
                setDefaultsAction(null);
              }}
            >
              {defaultsAction === 'save' ? (savingDefaults ? 'Saving…' : 'Save defaults') : 'Replace theme'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}

function ThemeOverview({
  theme,
  references,
  onOpen,
}: {
  theme: Theme;
  references: ReturnType<typeof collectThemeTokenReferences>;
  onOpen: (tab: ThemeTab) => void;
}): React.JSX.Element {
  return (
    <>
      {([
        ['colors', 'Colors', Object.keys(theme.colors)],
        ['fonts', 'Typography', Object.keys(theme.fonts)],
        ['radii', 'Radius', Object.keys(theme.radii)],
        ['elevations', 'Elevation', Object.keys(theme.elevations)],
        ['gradients', 'Gradient', Object.keys(theme.gradients)],
        ['buttons', 'Buttons', Object.keys(theme.buttons)],
      ] as const).map(([family, label, tokens]) => (
        <button
          key={family}
          type="button"
          className="w-full space-y-2 rounded-md border p-2.5 text-left hover:bg-secondary/30"
          onClick={() => onOpen(family)}
        >
          <span className="flex items-center justify-between text-xs font-medium">
            {label}
            <span className="font-normal text-muted-foreground">{tokens.length}</span>
          </span>
          <span className="flex min-h-5 items-center gap-1.5 overflow-hidden">
            {tokens.slice(0, 6).map((token) => <ThemeTokenSample key={token} compact family={family} token={token} theme={theme} />)}
            {tokens.length === 0 ? <span className="text-[11px] text-muted-foreground">No tokens</span> : null}
          </span>
          <span className="block text-[10px] text-muted-foreground">
            {references.filter((reference) => reference.family === family && reference.nodeId).length} component references
          </span>
        </button>
      ))}
      <p className="px-1 pt-1 text-[10px] leading-relaxed text-muted-foreground">
        Edits apply to this experience, update the canvas immediately, and share its undo history.
      </p>
    </>
  );
}

function TokenList({
  family,
  theme,
  assets,
  query,
  references,
  expanded,
  onExpandedChange,
  onChange,
  onDuplicate,
  onRename,
  onDelete,
}: {
  family: ThemeTokenFamily;
  theme: Theme;
  assets: Asset[];
  query: string;
  references: ReturnType<typeof collectThemeTokenReferences>;
  expanded: string | null;
  onExpandedChange: (token: string) => void;
  onChange: (token: string, value: Theme[ThemeTokenFamily][string], field: string) => void;
  onDuplicate: (token: string) => void;
  onRename: (token: string) => void;
  onDelete: (token: string) => void;
}): React.JSX.Element {
  const needle = query.trim().toLowerCase();
  const entries = Object.entries(theme[family]).filter(([token]) => token.toLowerCase().includes(needle));
  if (entries.length === 0) {
    return <p className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">No matching tokens.</p>;
  }

  return (
    <div className="space-y-1.5">
      {entries.map(([token, value]) => {
        const open = expanded === token;
        const count = references.filter((reference) => reference.family === family && reference.token === token).length;
        return (
          <div key={token} id={themeTokenAnchor(family, token)} tabIndex={-1} className="rounded-md border outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <div className="flex items-center gap-1 p-1.5">
              <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => onExpandedChange(token)}>
                <ThemeTokenSample compact family={family} token={token} theme={theme} />
                <span className="min-w-0 flex-1 truncate font-mono text-[10px]">{token}</span>
                <span className="text-[9px] tabular-nums text-muted-foreground">{count}</span>
                <ChevronDown className={`size-3 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="size-7 shrink-0" aria-label={`Actions for ${token}`}>
                    <MoreHorizontal className="size-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => onDuplicate(token)}><Copy /> Duplicate</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onRename(token)}>Rename</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => onDelete(token)}>
                    <Trash2 /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {open ? (
              <div className="space-y-2 border-t p-2">
                {family === 'colors' ? (
                  <ColorEditor token={token} value={value as Theme['colors'][string]} onChange={(next, field) => onChange(token, next, field)} />
                ) : family === 'fonts' ? (
                  <FontEditor token={token} value={value as FontToken} assets={assets} onChange={(next, field) => onChange(token, next, field)} />
                ) : family === 'radii' ? (
                  <RadiusEditor value={value as number | Corners} onChange={(next, field) => onChange(token, next, field)} />
                ) : family === 'elevations' ? (
                  <ElevationEditor token={token} value={value as Shadow} colors={Object.keys(theme.colors)} theme={theme} onChange={(next, field) => onChange(token, next, field)} />
                ) : family === 'gradients' ? (
                  <GradientEditor token={token} value={value as Gradient} colors={Object.keys(theme.colors)} theme={theme} onChange={(next, field) => onChange(token, next, field)} />
                ) : (
                  <ButtonEditor token={token} value={value as ButtonVariant} theme={theme} onChange={(next, field) => onChange(token, next, field)} />
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ColorEditor({ token, value, onChange }: { token: string; value: Theme['colors'][string]; onChange: (value: Theme['colors'][string], field: string) => void }): React.JSX.Element {
  return <div className="grid grid-cols-2 gap-2">{(['light', 'dark'] as const).map((mode) => (
    <label key={mode} className="space-y-1 text-[10px] capitalize text-muted-foreground">{mode}
      <span className="flex h-8 items-center gap-1 rounded-md bg-muted px-1.5">
        <input type="color" aria-label={`${token} ${mode}`} value={value[mode]} onChange={(event) => onChange({ ...value, [mode]: event.target.value }, mode)} className="size-5 bg-transparent" />
        <span className="truncate font-mono text-[9px] normal-case">{value[mode]}</span>
      </span>
    </label>
  ))}</div>;
}

function FontEditor({ token, value, assets, onChange }: { token: string; value: FontToken; assets: Asset[]; onChange: (value: FontToken, field: string) => void }): React.JSX.Element {
  const fontAssets = assets.filter((asset) => asset.content_type.startsWith('font/'));
  return <div className="space-y-2">
    <CompactField label="Family"><Input aria-label={`${token} family`} value={value.family} onChange={(event) => onChange({ ...value, family: event.target.value }, 'family')} className="h-7 text-xs" /></CompactField>
    <CompactField label="Font file"><select aria-label={`${token} font file`} value={value.src ?? ''} onChange={(event) => onChange({ ...value, src: event.target.value || undefined }, 'src')} className="h-7 w-full rounded-md border bg-background px-1.5 text-[10px]"><option value="">No bundled file</option>{fontAssets.map((asset) => <option key={asset.id} value={asset.id}>{assetLabel(asset)}</option>)}</select></CompactField>
    <div className="grid grid-cols-2 gap-2">
      <NumberField label="Size" value={value.size} onChange={(size) => onChange({ ...value, size }, 'size')} />
      <NumberField label="Weight" value={value.weight} step={100} onChange={(weight) => onChange({ ...value, weight }, 'weight')} />
      <NumberField label="Line height" value={value.line_height} step={0.1} onChange={(line_height) => onChange({ ...value, line_height }, 'line_height')} />
      <NumberField label="Max scale" value={value.max_scale} step={0.1} onChange={(max_scale) => onChange({ ...value, max_scale }, 'max_scale')} />
      {/* Tracking is in POINTS and is added between adjacent characters only (SPEC §6.4b). Bounded
          both ways at publish: past −2pt glyphs collide, past +10pt a heading fits no phone. */}
      <NumberField label="Tracking" value={value.letter_spacing ?? 0} step={0.1} onChange={(letter_spacing) => onChange({ ...value, letter_spacing: letter_spacing || undefined }, 'letter_spacing')} />
    </div>
    {/* ITALIC IS A TOKEN DECISION, not a per-node toggle — the same reasoning as weight. It also
        needs a real italic face in the font file above: synthetic oblique is forbidden (SPEC §4.1)
        because a skewed upright has different advances on every platform that skews it. */}
    <CompactField label="Style">
      <select
        aria-label={`${token} style`}
        value={value.style ?? 'normal'}
        onChange={(event) => onChange({ ...value, style: event.target.value === 'italic' ? 'italic' : undefined }, 'style')}
        className="h-7 w-full rounded-md border bg-background px-1.5 text-[10px]"
      >
        <option value="normal">Normal</option>
        <option value="italic">Italic</option>
      </select>
    </CompactField>
  </div>;
}

// A component token COMPOSES foundation tokens (ADR 0014): bg is a colour OR gradient token, radius a
// literal OR a `radii` token, plus an optional `elevations` token. Padding stays literal (spacing is
// not tokenized).
function ButtonEditor({ token, value, theme, onChange }: { token: string; value: ButtonVariant; theme: Theme; onChange: (value: ButtonVariant, field: string) => void }): React.JSX.Element {
  const colors = Object.keys(theme.colors);
  const gradients = Object.keys(theme.gradients);
  const radii = Object.keys(theme.radii);
  const elevations = Object.keys(theme.elevations);
  const bgValue = value.bg === null ? '' : typeof value.bg === 'string' ? `c:${value.bg}` : `g:${value.bg.gradient}`;
  const radiusIsToken = typeof value.radius === 'string';
  return <div className="space-y-2">
    <CompactField label="Background">
      <select
        aria-label={`${token} background`}
        value={bgValue}
        onChange={(event) => {
          const v = event.target.value;
          const bg = v === '' ? null : v.startsWith('g:') ? { gradient: v.slice(2) } : v.slice(2);
          onChange({ ...value, bg }, 'bg');
        }}
        className="h-7 w-full rounded-md border bg-background px-1.5 text-[10px]"
      >
        <option value="">None (text only)</option>
        <optgroup label="Colors">{colors.map((c) => <option key={c} value={`c:${c}`}>{c}</option>)}</optgroup>
        {gradients.length > 0 ? <optgroup label="Gradients">{gradients.map((g) => <option key={g} value={`g:${g}`}>{g}</option>)}</optgroup> : null}
      </select>
    </CompactField>
    <CompactField label="Foreground"><TokenSelect label={`${token} foreground`} value={value.fg} values={colors} onChange={(fg) => onChange({ ...value, fg }, 'fg')} /></CompactField>
    <CompactField label="Radius">
      <select
        aria-label={`${token} radius`}
        value={radiusIsToken ? (value.radius as string) : '__custom__'}
        onChange={(event) => {
          const v = event.target.value;
          onChange({ ...value, radius: v === '__custom__' ? (typeof value.radius === 'number' ? value.radius : 8) : v }, 'radius');
        }}
        className="h-7 w-full rounded-md border bg-background px-1.5 text-[10px]"
      >
        <option value="__custom__">Custom value</option>
        {radii.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
    </CompactField>
    {!radiusIsToken ? <NumberField label="Radius value" value={typeof value.radius === 'number' ? value.radius : 0} onChange={(radius) => onChange({ ...value, radius }, 'radius')} /> : null}
    <CompactField label="Elevation"><TokenSelect label={`${token} elevation`} value={value.elevation ?? ''} values={['', ...elevations]} onChange={(el) => onChange({ ...value, elevation: el || undefined }, 'elevation')} /></CompactField>
    <div className="grid grid-cols-2 gap-2">{(['top', 'end', 'bottom', 'start'] as const).map((side) => <NumberField key={side} label={`Padding ${side}`} value={value.padding[side]} onChange={(next) => onChange({ ...value, padding: { ...value.padding, [side]: next } }, `padding.${side}`)} />)}</div>
  </div>;
}

function RadiusEditor({ value, onChange }: { value: number | Corners; onChange: (value: number | Corners, field: string) => void }): React.JSX.Element {
  const uniform = typeof value === 'number';
  const corners = uniform ? { tl: value, tr: value, br: value, bl: value } : value;
  const cap = (v: number) => Math.min(v, 24);
  const previewCss = uniform ? cap(value) : `${cap(corners.tl)}px ${cap(corners.tr)}px ${cap(corners.br)}px ${cap(corners.bl)}px`;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="flex-1">
          <TokenSelect label="radius shape" value={uniform ? 'uniform' : 'corners'} values={['uniform', 'corners']} onChange={(mode) => onChange(mode === 'uniform' ? corners.tl : { ...corners }, 'shape')} />
        </span>
        <span aria-hidden className="size-8 shrink-0 border bg-muted" style={{ borderRadius: previewCss }} />
      </div>
      {uniform ? (
        <NumberField label="Radius" value={value} onChange={(next) => onChange(next, 'radius')} />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {(['tl', 'tr', 'bl', 'br'] as const).map((c) => (
            <NumberField key={c} label={c.toUpperCase()} value={corners[c]} onChange={(next) => onChange({ ...corners, [c]: next }, c)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ElevationEditor({ token, value, colors, theme, onChange }: { token: string; value: Shadow; colors: string[]; theme: Theme; onChange: (value: Shadow, field: string) => void }): React.JSX.Element {
  const c = theme.colors[value.color]?.light;
  return (
    <div className="space-y-2">
      <span aria-hidden className="block h-10 rounded-md border bg-background" style={{ boxShadow: `${value.x ?? 0}px ${value.y}px ${value.blur}px ${value.spread ?? 0}px ${c ?? 'rgba(0,0,0,0.3)'}` }} />
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="X" value={value.x ?? 0} min={-1000} onChange={(x) => onChange({ ...value, x }, 'x')} />
        <NumberField label="Y" value={value.y} min={-1000} onChange={(y) => onChange({ ...value, y }, 'y')} />
        <NumberField label="Blur" value={value.blur} onChange={(blur) => onChange({ ...value, blur }, 'blur')} />
        <NumberField label="Spread" value={value.spread ?? 0} onChange={(spread) => onChange({ ...value, spread }, 'spread')} />
      </div>
      <CompactField label="Color"><TokenSelect label={`${token} color`} value={value.color} values={colors} onChange={(color) => onChange({ ...value, color }, 'color')} /></CompactField>
    </div>
  );
}

function GradientEditor({ token, value, colors, theme, onChange }: { token: string; value: Gradient; colors: string[]; theme: Theme; onChange: (value: Gradient, field: string) => void }): React.JSX.Element {
  const strip = `linear-gradient(${value.angle}deg, ${value.stops.map((s) => theme.colors[s]?.light ?? 'transparent').join(', ')})`;
  return (
    <div className="space-y-2">
      <span aria-hidden className="block h-8 rounded-md border" style={{ backgroundImage: strip }} />
      <NumberField label="Angle" value={value.angle} max={360} onChange={(angle) => onChange({ ...value, angle }, 'angle')} />
      {value.stops.map((stop, i) => (
        <div key={i} className="flex items-center gap-1">
          <span className="flex-1">
            <TokenSelect label={`${token} stop ${i + 1}`} value={stop} values={colors} onChange={(next) => onChange({ ...value, stops: value.stops.map((s, j) => (j === i ? next : s)) }, `stops.${i}`)} />
          </span>
          {/* The schema floor is 2 stops — a one-stop gradient is a solid colour with extra steps. */}
          {value.stops.length > 2 ? (
            <button type="button" aria-label={`Remove stop ${i + 1}`} className="text-muted-foreground hover:text-foreground" onClick={() => onChange({ ...value, stops: value.stops.filter((_, j) => j !== i) }, 'stops')}>
              <X className="size-3" />
            </button>
          ) : null}
        </div>
      ))}
      {value.stops.length < 8 ? (
        <button type="button" className="text-left text-[11px] text-muted-foreground hover:text-foreground" onClick={() => onChange({ ...value, stops: [...value.stops, value.stops[value.stops.length - 1]!] }, 'stops')}>
          + stop
        </button>
      ) : null}
    </div>
  );
}

function CompactField({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return <label className="block space-y-1 text-[10px] text-muted-foreground"><span>{label}</span>{children}</label>;
}

function NumberField({ label, value, step = 1, min = 0, max, onChange }: { label: string; value: number; step?: number; min?: number; max?: number; onChange: (value: number) => void }): React.JSX.Element {
  return <CompactField label={label}><Input type="number" aria-label={label} min={min} max={max} step={step} value={value} onChange={(event) => { const next=event.target.valueAsNumber; if(Number.isFinite(next)) onChange(next); }} className="h-7 text-xs" /></CompactField>;
}

function TokenSelect({ label, value, values, onChange }: { label: string; value: string; values: string[]; onChange: (value: string) => void }): React.JSX.Element {
  return <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value)} className="h-7 w-full rounded-md border bg-background px-1.5 text-[10px]">{values.map((token) => <option key={token || 'none'} value={token}>{token || 'None'}</option>)}</select>;
}

function DeleteTokenDialog({ target, theme, referenceCount, replacement, onReplacementChange, onClose, onConfirm }: { target: TokenTarget | null; theme: Theme; referenceCount: number; replacement: string; onReplacementChange: (value: string) => void; onClose: () => void; onConfirm: () => void }): React.JSX.Element {
  const choices = target ? Object.keys(theme[target.family]).filter((token) => token !== target.token) : [];
  return <Dialog open={target !== null} onOpenChange={(open) => !open && onClose()}><DialogContent><DialogHeader><DialogTitle>Delete “{target?.token}”?</DialogTitle><DialogDescription>{referenceCount > 0 ? `${referenceCount} reference${referenceCount === 1 ? '' : 's'} must be moved to another ${target ? FAMILY_SINGULAR[target.family] : ""} token.` : 'This token is unused in the current experience.'}</DialogDescription></DialogHeader>{referenceCount > 0 ? <CompactField label="Replacement"><TokenSelect label="Replacement token" value={replacement} values={['', ...choices]} onChange={onReplacementChange} /></CompactField> : null}<DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="destructive" disabled={referenceCount > 0 && !replacement} onClick={onConfirm}>Delete token</Button></DialogFooter></DialogContent></Dialog>;
}

function assetLabel(asset: Asset): string {
  return (asset.r2_key.split('/').pop() ?? asset.r2_key).replace(/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}-/i, '');
}

/** MINTING MAKES ORPHANS POSSIBLE; THIS MAKES THEM REVERSIBLE.
 *
 *  Picking a colour now creates a token, so an author who experiments leaves tokens behind — the
 *  predictable cost of removing the six-step detour through this panel. `unusedColorTokens` reuses
 *  the SAME reference walk the delete dialog already trusts, so a colour referenced only from inside
 *  an elevation or a gradient still counts as used and cannot be swept away.
 *
 *  Shown only when there is something to say. A permanent "0 unused" row would be noise on every
 *  theme that never mints. */
function UnusedColors({
  doc,
  apply,
}: {
  doc: AuthoringDoc;
  apply: (mutate: (draft: AuthoringDoc) => void, mergeKey?: string) => void;
}): React.JSX.Element | null {
  const unused = unusedColorTokens(doc);
  if (unused.length === 0) return null;

  return (
    <div className="mb-2 flex items-center gap-2 rounded-md border border-dashed px-2 py-1.5 text-[11px] text-muted-foreground">
      <span className="min-w-0 flex-1 truncate" title={unused.join(', ')}>
        {unused.length} unused {unused.length === 1 ? 'colour' : 'colours'}
      </span>
      <button
        type="button"
        aria-label="remove unused colours"
        onClick={() =>
          apply((draft) => {
            // Recomputed against the DRAFT, not the render-time list: an edit between render and
            // click could have put one back into use, and deleting a live token would break publish.
            for (const token of unusedColorTokens(draft)) delete draft.theme.colors[token];
          }, 'theme.colors.cleanup')
        }
        className="shrink-0 rounded px-1.5 py-0.5 text-foreground transition-colors hover:bg-muted"
      >
        Remove
      </button>
    </div>
  );
}
